<template>
    <div>
        <!-- Main Subscription -->
        <div class="card mb-3 subscription-container shadow">
            <div class="card-header">
                <h5>ORM Subscription</h5>
            </div>
            <div class="card-body">
                <div v-if="team.subscription_ends_at" class="subscription-row">
                    <date-picker @date-picker-updated="changeMainSubscriptionDate"
                        :startDate="rawSubscriptionEndDates.mainSubscriptionEndDateRaw" />
                    <button class="btn btn-primary" @click="updateMainSubscriptionPrompt" :disabled="isUpdating">
                        <span v-if="isUpdating">Updating..</span>
                        <span v-else>Update Subscription</span>
                    </button>
                </div>
                <div class="mt-2" v-if="team.subscription_ends_at">
                    <p v-if="team.status !== 'expired'">
                        <strong>Active until:</strong> {{ formattedSubscriptionEndDates.mainSubscriptionEndDate }} (Plan: {{ subscriptionPlans.mainPlanStatus }})
                    </p>
                    <p v-else>
                        <strong>Expired at:</strong> {{ formattedSubscriptionEndDates.mainSubscriptionEndDate }} (Plan: {{ subscriptionPlans.mainPlanStatus }})
                    </p>
                </div>
                <div v-else class="subscription-row">
                    <date-picker @date-picker-updated="changeMainSubscriptionDate"
                        :startDate="rawSubscriptionEndDates.newMainSubscriptionEndDate" />
                    <button class="btn btn-gradient-success btn-success" @click="addMainSubscriptionPrompt">
                        Add Subscription
                    </button>
                </div>
            </div>
        </div>

        <!-- Clickhint Subscription -->
        <div class="card mb-3 subscription-container shadow">
            <div class="card-header">
                <h5>Clickhint Subscription</h5>
            </div>
            <div class="card-body">
                <div class="subscription-row">
                    <select class="mr-3" v-model="clickhintSubscriptionPlan">
                        <option value="free">Free Trial</option>
                        <option value="clickhint-pro">Subscription</option>
                    </select>
                    <date-picker ref="subcription_date_picker" class="mr-3"
                        @date-picker-updated="changeNewClickhintSubscriptionDate"
                        :startDate="rawSubscriptionEndDates.newClickhintSubscriptionEndDate" />
                    <button class="btn btn-success" @click="addClickhintSubscriptionPrompt" :disabled="isUpdating"
                        v-if="team.clickhint_status === 'none'">
                        <span v-if="isUpdating"><i class="fa fa-btn fa-spinner fa-spin"></i> Saving</span>
                        <span v-else>Add Subscription</span>
                    </button>
                    <button class="btn btn-primary" @click="updateClickhintSubscription" v-else>
                        <span v-if="isUpdating"><i class="fa fa-btn fa-spinner fa-spin"></i> Updating</span>
                        <span v-else>Update Subscription</span>
                    </button>
                </div>
                <div class="mt-2" v-if="team.clickhint_subscription_ends_at">
                    <p v-if="team.clickhint_status === 'active'">
                        <strong>Active Until:</strong>
                        {{ formattedSubscriptionEndDates.clickhintSubscriptionEndDate }} (Plan: {{ team.clickhint_plan }})
                    </p>
                    <p v-else-if="team.clickhint_status === 'expired'">
                        <strong>Expired at:</strong> {{ formattedSubscriptionEndDates.clickhintSubscriptionEndDate }} (Plan: {{ team.clickhint_plan }})
                    </p>
                </div>
            </div>
        </div>

        <!-- Case Subscription -->
        <div class="card mb-3 subscription-container shadow">
            <div class="card-header">
                <h5>Case Subscription</h5>
            </div>
            <div class="card-body">
                <div v-if="team.case_subscription_ends_at">
                    <div class="subscription-row">
                        <select class="mr-3" v-model="caseSubscriptionPlan">
                            <option value="free">Free Trial</option>
                            <option value="subscription">Subscription</option>
                        </select>
                        <date-picker @date-picker-updated="changeCaseSubscriptionDate"
                            :startDate="rawSubscriptionEndDates.caseSubscriptionEndDateRaw" />
                        <button class="btn btn-primary" @click="updateCaseSubscriptionPrompt" :disabled="isUpdating">
                            <span v-if="isUpdating">Updating..</span>
                            <span v-else>Update Subscription</span>
                        </button>
                    </div>
                    <div class="mt-2">
                        <p v-if="team.case_status !== 'expired'">
                            <strong>Active until:</strong> {{ formattedSubscriptionEndDates.caseSubscriptionEndDate }} (Plan: {{ team.case_plan }})
                        </p>
                        <p v-else>
                            <strong>Expired at:</strong> {{ formattedSubscriptionEndDates.caseSubscriptionEndDate }} (Plan: {{ team.case_plan }})
                        </p>
                    </div>
                </div>
                <div v-else class="subscription-row">
                    <select class="mr-3" v-model="caseSubscriptionPlan">
                        <option value="free">Free</option>
                        <option value="subscribed">Subscribed</option>
                    </select>
                    <date-picker @date-picker-updated="changeCaseSubscriptionDate"
                        :startDate="rawSubscriptionEndDates.newCaseSubscriptionEndDate" />
                    <button class="btn btn-gradient-success btn-success" @click="addCaseSubscriptionPrompt">
                        Add Subscription
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import AdministrationMixin from '../AdministrationMixin';

export default {
    props: {
        team: Object,
    },
    mixins: [AdministrationMixin],
    data() {
        return {
            newMainSubscriptionDate: null, // Stores the date selected for updating or adding the main ORM subscription.
            newCaseSubscriptionDate: null, // Stores the date selected for updating or adding the Case subscription.
            newClickhintSubscriptionDate: null, // Stores the date selected for updating or adding the Clickhint subscription.
            isUpdating: false, // A boolean flag indicating if an update or add operation is currently in progress.
            clickhintSubscriptionPlan: 'free', // The selected plan for the Clickhint subscription.
            caseSubscriptionPlan: 'free', // The selected plan for the Case subscription.
        };
    },
    computed: {
        /*
         * Determines the status of the main ORM subscription plan.
         */
        subscriptionPlans() {
            return {
                mainPlanStatus: moment(this.team.subscription_ends_at) > moment(this.team.trial_ends_at) ? 'subscribed' : 'trial',
            };
        },
        /*
         * Computes and returns formatted end dates for various subscriptions as display strings.
         * Handles invalid dates by returning an empty string.
         */
        formattedSubscriptionEndDates() {
            const overallDate = moment.max(
                moment(this.team.subscription_ends_at || 0),
                moment(this.team.trial_ends_at || 0)
            );
            const clickhintDate = moment(this.team.clickhint_subscription_ends_at);
            const caseDate = moment(this.team.case_subscription_ends_at);

            return {
                mainSubscriptionEndDate: overallDate.isValid() ? overallDate.format('D MMM YYYY') : '',
                clickhintSubscriptionEndDate: clickhintDate.isValid() ? clickhintDate.format('D MMM YYYY') : '',
                caseSubscriptionEndDate: caseDate.isValid() ? caseDate.format('D MMM YYYY') : '',
            };
        },

        /*
         * Computes and returns raw moment objects for various subscription end dates.
         * Includes current actual end dates and projected new end dates.
         */
        rawSubscriptionEndDates() {
            return {
                mainSubscriptionEndDateRaw: moment(this.team.subscription_ends_at), // Raw moment object for the current overall ORM subscription end date.
                clickhintSubscriptionEndDateRaw: moment(this.team.clickhint_subscription_ends_at), // Raw moment object for the current ClickHint subscription end date.
                caseSubscriptionEndDateRaw: moment(this.team.case_subscription_ends_at), // Raw moment object for the current Case subscription end date.

                newMainSubscriptionEndDate: moment().add(1, 'year'), // Projected new overall ORM subscription end date (1 year from now).
                newClickhintSubscriptionEndDate: this.clickhintSubscriptionPlan === 'clickhint-pro' // Projected new ClickHint subscription end date based on the selected plan.
                    ? moment().add(1, 'year')
                    : moment().add(1, 'month'),
                newCaseSubscriptionEndDate: this.caseSubscriptionPlan === 'pro' // Projected new Case subscription end date based on the selected plan.
                    ? moment().add(1, 'year')
                    : moment().add(1, 'month'),
            };
        },
    },
    watch: {
        /*
         * Initializes new subscription dates when the 'team' prop changes.
         */
        team: {
            immediate: true,
            deep: true,
            handler() {
                this.newMainSubscriptionDate = this.team.subscription_ends_at
                    ? moment(this.team.subscription_ends_at).format('YYYY-MM-DD')
                    : moment().add(1, 'year').format('YYYY-MM-DD');
                this.newCaseSubscriptionDate = this.team.case_subscription_ends_at
                    ? moment(this.team.case_subscription_ends_at).format('YYYY-MM-DD')
                    : moment().add(1, 'year').format('YYYY-MM-DD');
                this.newClickhintSubscriptionDate = this.team.clickhint_subscription_ends_at
                    ? moment(this.team.clickhint_subscription_ends_at).format('YYYY-MM-DD')
                    : (this.clickhintSubscriptionPlan === 'clickhint-pro' ? moment().add(1, 'year') : moment().add(1, 'month')).format('YYYY-MM-DD');
            },
        },
    },
    methods: {
        /*
         * Updates the new main subscription date.
         */
        changeMainSubscriptionDate(date) {
            this.newMainSubscriptionDate = date;
        },
        /*
         * Updates the new case subscription date.
         */
        changeCaseSubscriptionDate(date) {
            this.newCaseSubscriptionDate = date;
        },
        /*
         * Updates the new Clickhint subscription date.
         */
        changeNewClickhintSubscriptionDate(date) {
            this.newClickhintSubscriptionDate = date;
        },

        // Main Subscription Actions
        /*
         * Prompts for confirmation before updating the main ORM subscription.
         */
        updateMainSubscriptionPrompt() {
            swal(
                {
                    title: `Is the new main subscription date correct? (${this.newMainSubscriptionDate})`,
                    text: 'The change will apply immediately!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes!',
                },
                async (result) => {
                    if (result) {
                        this.updateMainSubscription();
                    }
                },
            );
        },
        /*
         * Sends an API request to update the main ORM subscription date.
         */
        async updateMainSubscription() {
            const postData = {
                subscription_id: this.team.subscription_id,
                new_date: this.newMainSubscriptionDate,
            };
            this.isUpdating = true;
            await axios.put(this.updateSubscriptionUrl, postData);
            this.isUpdating = false;
            this.$emit('update-triggered', true);
        },
        /*
         * Prompts for confirmation before adding a new main ORM subscription.
         */
        addMainSubscriptionPrompt() {
            swal(
                {
                    title: `Is the new subscription date correct: ${this.newMainSubscriptionDate}?`,
                    text: 'The subscription will be created immediately!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Create!',
                },
                async (result) => {
                    if (result) {
                        await this.createMainSubscription();
                    }
                },
            );
        },
        /*
         * Sends an API request to create a new main ORM subscription.
         */
        async createMainSubscription() {
            const postData = {
                new_date: this.newMainSubscriptionDate,
            };
            this.isUpdating = true;
            await axios.post(this.createSubscriptionUrl, postData);
            this.isUpdating = false;
            this.$emit('update-triggered', true);
        },

        // Clickhint Subscription Actions
        /*
         * Prompts for confirmation before adding a new Clickhint subscription.
         */
        addClickhintSubscriptionPrompt() {
            swal(
                {
                    title: `Is the new Clickhint subscription date correct: ${this.newClickhintSubscriptionDate}?`,
                    text: 'The subscription will be created immediately!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Create!',
                },
                async (result) => {
                    if (result) {
                        await this.createClickhintSubscription();
                    }
                },
            );
        },
        /*
         * Sends an API request to create a new Clickhint subscription.
         */
        async createClickhintSubscription() {
            const postData = {
                new_date: this.newClickhintSubscriptionDate,
                type: this.clickhintSubscriptionPlan,
            };
            this.isUpdating = true;
            await axios.post(this.createClickhintUrl, postData);
            this.isUpdating = false;
            this.$emit('update-triggered', true);
        },
        /*
         * Sends an API request to update an existing Clickhint subscription.
         */
        async updateClickhintSubscription() {
            const postData = {
                subscription_id: this.team.clickhint_subscription_id,
                new_date: this.newClickhintSubscriptionDate,
                type: this.clickhintSubscriptionPlan,
            };
            this.isUpdating = true;
            await axios.put(this.updateClickhintUrl, postData);
            this.isUpdating = false;
            this.$emit('update-triggered', true);
        },

        // Case Subscription Actions
        /*
         * Sends an API request to create a new Case subscription.
         */
        async createCaseSubscription() {
            const postData = {
                new_date: this.newCaseSubscriptionDate,
                type: this.caseSubscriptionPlan,
            };
            this.isUpdating = true;
            await axios.post(this.createCaseSubscriptionUrl, postData);
            this.isUpdating = false;
            this.$emit('update-triggered', true);
        },
        /*
         * Sends an API request to update an existing Case subscription.
         */
        async updateCaseSubscription() {
            const postData = {
                subscription_id: this.team.case_subscription_id,
                new_date: this.newCaseSubscriptionDate,
                type: this.caseSubscriptionPlan,
            };
            this.isUpdating = true;
            await axios.put(this.updateCaseSubscriptionUrl, postData);
            this.isUpdating = false;
            this.$emit('update-triggered', true);
        },
        /*
         * Prompts for confirmation before adding a new Case subscription.
         */
        addCaseSubscriptionPrompt() {
            swal(
                {
                    title: `Is the new case subscription date correct: ${this.newCaseSubscriptionDate}?`,
                    text: 'The case subscription will be created immediately!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Create!',
                },
                async (result) => {
                    if (result) {
                        await this.createCaseSubscription();
                    }
                },
            );
        },
        /*
         * Prompts for confirmation before updating an existing Case subscription.
         */
        updateCaseSubscriptionPrompt() {
            swal(
                {
                    title: `Is the new case subscription date correct: ${this.newCaseSubscriptionDate}?`,
                    text: 'The case subscription will be updated immediately!',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Update!',
                },
                async (result) => {
                    if (result) {
                        await this.updateCaseSubscription();
                    }
                },
            );
        },
    },
};
</script>

<style scoped>
/* Add consistent row styles with maximum width for neat appearance */
.subscription-container {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
}

.subscription-row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.subscription-row select,
.subscription-row button,
.subscription-row .date-picker {
    max-width: 300px;
}
</style>
